import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bot, Copy, Loader2, Sparkles, Trash2, User, X } from 'lucide-react'

import { extractTextOutput } from '@/api/ai'
import {
  applyWriterScriptAI,
  runWriterScriptAgent,
  type WriterAgentLayer,
  type WriterAgentOutputKind,
  type WriterScriptAgentSkill,
} from '@/api/studio-writer'
import { AuthSessionExpiredError } from '@/api/authSession'
import { pollAITask } from '@/features/studio/hooks/pollAITask'
import { copyTextToClipboard } from '@/shared/lib/clipboard'
import { notifyPointsRefresh } from '@/shared/lib/points-refresh'
import { toast } from '@/shared/lib/notify'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { Button } from '@/shared/components/ui/button'
import { ScriptAIModelField } from '@/features/studio/components/ScriptAIModelField'
import { ProtoBtn, ProtoCard, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { useScriptTextModel } from '@/features/studio/hooks/useScriptTextModel'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { appendWriterNextScene } from '@/features/studio/script/append-next-scene'
import type { ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'
import type { Script } from '@/api/studio-production'

export type { WriterAgentLayer, WriterAgentOutputKind }

export type WriterAgentMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  skill?: WriterScriptAgentSkill
  label?: string
  cost?: number
  writable?: boolean
  outputKind?: WriterAgentOutputKind
  createdAt: number
}

const SKILLS_BY_LAYER: Record<WriterAgentLayer, WriterScriptAgentSkill[]> = {
  series: ['series_outline', 'continuity_check', 'freeform'],
  story: ['story_content', 'continuity_check', 'freeform'],
  episode: [
    'episode_outline',
    'continue_scene',
    'episode_scene_list',
    'polish',
    'continuity_check',
    'freeform',
  ],
}

const MAX_CHAT_MESSAGES = 40

function defaultSkill(
  layer: WriterAgentLayer,
  hasSeriesOutline: boolean,
  hasStoryContent: boolean,
  hasEpisodeOutline: boolean,
): WriterScriptAgentSkill {
  if (layer === 'series') return 'series_outline'
  if (layer === 'story') return 'story_content'
  if (!hasSeriesOutline && !hasStoryContent) return 'episode_outline'
  if (!hasEpisodeOutline) return 'episode_outline'
  return 'continue_scene'
}

function skillBlockReason(
  skill: WriterScriptAgentSkill,
  layer: WriterAgentLayer,
  hasSeriesOutline: boolean,
  hasStoryContent: boolean,
  hasEpisodeOutline: boolean,
): string | null {
  const needsBible = !hasSeriesOutline && !hasStoryContent
  if (skill === 'series_outline' || skill === 'story_content') return null
  if (skill === 'continuity_check') return null
  if (skill === 'episode_outline') {
    return needsBible ? 'writer.editor.agentNeedSeriesOrStory' : null
  }
  if (
    skill === 'continue_scene' ||
    skill === 'episode_scene_list' ||
    skill === 'polish' ||
    (skill === 'freeform' && layer === 'episode')
  ) {
    if (needsBible) return 'writer.editor.agentNeedSeriesOrStory'
    if (!hasEpisodeOutline) return 'writer.editor.agentNeedEpisodeOutline'
  }
  return null
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function loadChat(key: string): WriterAgentMessage[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw) as WriterAgentMessage[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveChat(key: string, items: WriterAgentMessage[]) {
  try {
    localStorage.setItem(key, JSON.stringify(items.slice(-MAX_CHAT_MESSAGES)))
  } catch {
    /* quota */
  }
}

function formatTime(ts: number, locale: string) {
  return new Date(ts).toLocaleString(locale, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function defaultMessage(skill: WriterScriptAgentSkill, t: (key: string) => string) {
  switch (skill) {
    case 'series_outline':
      return t('writer.editor.agentDefaultSeries')
    case 'story_content':
      return t('writer.editor.agentDefaultStory')
    case 'episode_outline':
      return t('writer.editor.agentDefaultEpisodeOutline')
    case 'continue_scene':
      return t('writer.editor.agentDefaultContinue')
    case 'episode_scene_list':
      return t('writer.editor.agentDefaultSceneList')
    default:
      return t('writer.editor.agentDefaultCheck')
  }
}

export function WriterScriptEditorAIPanel({
  scriptId,
  episodeNo,
  layer = 'episode',
  body,
  sceneNo,
  convention = 'cn',
  isDraft = true,
  hasSeriesOutline = false,
  hasStoryContent = false,
  hasEpisodeOutline = false,
  onApplied,
  onApplySeriesOutline,
  onApplyStoryContent,
  onApplyEpisodeOutline,
  onError,
}: {
  scriptId: number
  episodeNo: number
  layer?: WriterAgentLayer
  body: string
  sceneNo?: number | null
  convention?: ScriptConvention
  isDraft: boolean
  hasSeriesOutline?: boolean
  hasStoryContent?: boolean
  hasEpisodeOutline?: boolean
  onApplied: (script: Script | null, text?: string, opts?: { selectLastScene?: boolean }) => void
  onApplySeriesOutline?: (text: string) => Promise<void>
  onApplyStoryContent?: (text: string) => Promise<void>
  onApplyEpisodeOutline?: (text: string) => Promise<void>
  onError: (message: string) => void
}) {
  const { t, i18n } = useTranslation()
  const scriptModel = useScriptTextModel()
  const { modelId, selectedModel, ready: aiModelReady } = scriptModel
  const [prompt, setPrompt] = useState('')
  const [activeSkill, setActiveSkill] = useState<WriterScriptAgentSkill>(() =>
    defaultSkill(layer, hasSeriesOutline, hasStoryContent, hasEpisodeOutline),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [clearChatOpen, setClearChatOpen] = useState(false)
  const chatEndRef = useRef<HTMLLIElement>(null)

  const skills = SKILLS_BY_LAYER[layer]
  const chatKey =
    layer === 'episode'
      ? `writer-script-agent:${scriptId}:episode:${episodeNo}`
      : `writer-script-agent:${scriptId}:${layer}`
  const [messages, setMessages] = useState<WriterAgentMessage[]>(() => loadChat(chatKey))

  useEffect(() => {
    setMessages(loadChat(chatKey))
    setPrompt('')
    setError(null)
    setActiveSkill(defaultSkill(layer, hasSeriesOutline, hasStoryContent, hasEpisodeOutline))
  }, [chatKey, layer, hasSeriesOutline, hasStoryContent, hasEpisodeOutline])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, busy])

  const persistMessages = useCallback(
    (next: WriterAgentMessage[]) => {
      setMessages(next)
      saveChat(chatKey, next)
    },
    [chatKey],
  )

  const lastWritableAssistant = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]
      if (m.role === 'assistant' && m.writable !== false && m.content.trim()) {
        return m
      }
    }
    return null
  }, [messages])

  const agentHistory = useMemo(
    () =>
      messages.slice(-12).map((m) => ({
        role: m.role,
        content: m.content,
      })),
    [messages],
  )

  function costLabel(base: string) {
    if (!selectedModel) return base
    return `${base}（${t('script.aiModelCostUnit', { cost: selectedModel.cost })}）`
  }

  function skillLabel(skill: WriterScriptAgentSkill) {
    return t(`writer.editor.agentSkill.${skill}`)
  }

  function skillNeedsPrompt(skill: WriterScriptAgentSkill) {
    return skill === 'freeform' || skill === 'polish' || skill === 'continuity_check'
  }

  function applyButtonLabel(outputKind?: WriterAgentOutputKind, skill?: WriterScriptAgentSkill) {
    if (outputKind === 'series_outline') return t('writer.editor.aiApplySeriesOutline')
    if (outputKind === 'story_content') return t('writer.editor.aiApplyStoryContent')
    if (outputKind === 'episode_outline') return t('writer.editor.aiApplyEpisodeOutline')
    if (outputKind === 'scene_list') return t('writer.editor.aiApplySceneList')
    if (outputKind === 'next_scene' || skill === 'continue_scene') return t('writer.editor.aiApplyNextScene')
    return t('writer.editor.aiApply')
  }

  function canApplyAssistant(msg: WriterAgentMessage | null) {
    if (!msg?.content.trim()) return false
    if (msg.outputKind === 'series_outline') return !!onApplySeriesOutline
    if (msg.outputKind === 'story_content') return !!onApplyStoryContent
    if (msg.outputKind === 'episode_outline') return isDraft && !!onApplyEpisodeOutline
    return isDraft
  }

  function blockReasonFor(skill: WriterScriptAgentSkill) {
    return skillBlockReason(skill, layer, hasSeriesOutline, hasStoryContent, hasEpisodeOutline)
  }

  async function handleSend(skillOverride?: WriterScriptAgentSkill) {
    const trimmed = prompt.trim()
    const skill = skillOverride ?? activeSkill
    const blocked = blockReasonFor(skill)
    if (blocked) {
      setError(t(blocked))
      return
    }
    if (skillNeedsPrompt(skill) && !trimmed) return

    const userContent = trimmed || defaultMessage(skill, t)
    const userMsg: WriterAgentMessage = {
      id: newId(),
      role: 'user',
      content: userContent,
      skill,
      label: skillLabel(skill),
      createdAt: Date.now(),
    }

    setBusy(true)
    setError(null)
    setCopiedMessageId(null)

    const pending = [...messages, userMsg]
    persistMessages(pending)

    try {
      const res = await runWriterScriptAgent(scriptId, episodeNo, {
        skill,
        layer,
        message: userContent,
        model_id: modelId || undefined,
        context:
          layer === 'episode'
            ? skill === 'continue_scene'
              ? body.slice(-4000)
              : body.slice(0, 4000)
            : undefined,
        scene_no: layer === 'episode' && skill !== 'continue_scene' ? (sceneNo ?? undefined) : undefined,
        history: agentHistory,
      })

      const final = await pollAITask(res.task_id, () => {}, {
        taskType: 'text',
        modelId: modelId || undefined,
      })
      if (final.status !== 'succeeded') {
        throw new Error(final.error_message || t('script.aiFailed'))
      }
      notifyPointsRefresh()

      const text = extractTextOutput(final.output).trim()
      if (!text) throw new Error(t('script.aiFailed'))

      const assistantMsg: WriterAgentMessage = {
        id: newId(),
        role: 'assistant',
        content: text,
        skill: res.skill as WriterScriptAgentSkill,
        label: res.label || skillLabel(skill),
        cost: final.cost ?? res.cost,
        writable: res.writable,
        outputKind: (res.output_kind as WriterAgentOutputKind | undefined) ?? 'script',
        createdAt: Date.now(),
      }
      persistMessages([...pending, assistantMsg])
      setPrompt('')
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      const msg = t(resolveApiErrorMessage(e, 'writer.editor.aiFailed'))
      setError(msg)
      onError(msg)
      persistMessages(messages)
    } finally {
      setBusy(false)
    }
  }

  async function handleApplyLast() {
    const target = lastWritableAssistant
    if (!target?.content.trim()) return
    setBusy(true)
    setError(null)
    try {
      if (target.outputKind === 'series_outline') {
        if (!onApplySeriesOutline) {
          setError(t('writer.editor.agentApplySeriesOutlineMissing'))
          return
        }
        await onApplySeriesOutline(target.content)
        return
      }
      if (target.outputKind === 'story_content') {
        if (!onApplyStoryContent) {
          setError(t('writer.editor.agentApplyStoryContentMissing'))
          return
        }
        await onApplyStoryContent(target.content)
        return
      }
      if (target.outputKind === 'episode_outline') {
        if (!isDraft) {
          setError(t('writer.editor.agentApplyDraftOnly'))
          return
        }
        if (!onApplyEpisodeOutline) {
          setError(t('writer.editor.agentApplyEpisodeOutlineMissing'))
          return
        }
        await onApplyEpisodeOutline(target.content)
        return
      }
      if (!isDraft) {
        setError(t('writer.editor.agentApplyDraftOnly'))
        return
      }
      const isNextScene =
        target.outputKind === 'next_scene' || target.skill === 'continue_scene'
      const shouldAppend = target.outputKind === 'scene_list' || isNextScene
      const assembled = isNextScene
        ? appendWriterNextScene(body, target.content, convention)
        : {
            nextBody:
              shouldAppend && body.trim()
                ? `${body.trimEnd()}\n\n${target.content.trim()}`
                : target.content,
            sceneNo: sceneNo ?? 0,
          }
      const res = await applyWriterScriptAI(scriptId, episodeNo, {
        text: assembled.nextBody,
      })
      onApplied(res.script ?? null, res.text ?? assembled.nextBody, {
        selectLastScene: isNextScene,
      })
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      const msg = t(resolveApiErrorMessage(e, 'writer.editor.aiFailed'))
      setError(msg)
      onError(msg)
    } finally {
      setBusy(false)
    }
  }

  async function handleCopy(messageId: string, text: string) {
    if (!text.trim()) return
    try {
      await copyTextToClipboard(text)
      setCopiedMessageId(messageId)
      window.setTimeout(() => setCopiedMessageId((current) => (current === messageId ? null : current)), 2000)
    } catch {
      toast.error(t('script.aiCopyFailed'))
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <ProtoCard className="flex min-h-0 flex-1 flex-col !p-3">
        <div className="flex shrink-0 items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-200">
            <Bot className="h-3.5 w-3.5 text-indigo-400" />
            {t(`writer.editor.agentLayer.${layer}`)}
          </p>
          <div className="flex items-center gap-1.5">
            {messages.length > 0 ? (
              <ProtoBtn size="sm" variant="ghost" className="text-zinc-500" onClick={() => setClearChatOpen(true)}>
                <Trash2 className="h-3.5 w-3.5" />
                {t('writer.editor.agentClearChat')}
              </ProtoBtn>
            ) : null}
          </div>
        </div>
        {layer === 'story' && !hasSeriesOutline ? (
          <p className="mt-2 shrink-0 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-200/90">
            {t('writer.editor.agentStoryNoOutlineHint')}
          </p>
        ) : null}
        {layer === 'episode' && !hasSeriesOutline && !hasStoryContent ? (
          <p className="mt-2 shrink-0 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-200/90">
            {t('writer.editor.agentNeedSeriesOrStory')}
          </p>
        ) : null}
        {layer === 'episode' && (hasSeriesOutline || hasStoryContent) && !hasEpisodeOutline ? (
          <p className="mt-2 shrink-0 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-200/90">
            {t('writer.editor.agentNeedEpisodeOutline')}
          </p>
        ) : null}

        <ul className="studio-scrollbar-hidden mt-2 min-h-0 flex-1 space-y-2 overflow-y-auto">
          {messages.length === 0 ? (
            <li className="rounded-lg border border-dashed border-white/[0.08] bg-[#0e0e16]/60 px-3 py-4 text-[11px] leading-relaxed whitespace-pre-line text-zinc-500">
              {t(`writer.editor.agentEmpty.${layer}`)}
            </li>
          ) : (
            messages.map((m) => (
              <li
                key={m.id}
                className={`rounded-lg border px-2.5 py-2 ${
                  m.role === 'user'
                    ? 'border-indigo-500/20 bg-indigo-500/5'
                    : 'border-white/[0.06] bg-[#0e0e16]'
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                  {m.role === 'user' ? <User className="h-3 w-3" /> : <Bot className="h-3.5 w-3.5 text-indigo-400" />}
                  <span>{m.role === 'user' ? t('writer.editor.agentRoleUser') : m.label || t('writer.editor.agentRoleAssistant')}</span>
                  <span>· {formatTime(m.createdAt, i18n.language)}</span>
                  {m.cost != null ? <span>· {t('script.aiCost', { cost: m.cost })}</span> : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-zinc-200">{m.content}</p>
                {m.role === 'assistant' && m.writable !== false ? (
                  <div className="mt-2 flex gap-1.5">
                    <ProtoBtn size="sm" variant="secondary" onClick={() => void handleCopy(m.id, m.content)}>
                      <Copy className="h-3 w-3" />
                      {copiedMessageId === m.id ? t('script.aiCopied') : t('script.aiCopyResult')}
                    </ProtoBtn>
                  </div>
                ) : null}
              </li>
            ))
          )}
          {busy ? (
            <li className="flex items-center gap-2 px-1 text-[11px] text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-400" />
              {t('writer.editor.agentThinking')}
            </li>
          ) : null}
          <li ref={chatEndRef} />
        </ul>

        {lastWritableAssistant && canApplyAssistant(lastWritableAssistant) ? (
          <div className="mt-2 flex shrink-0 gap-2 border-t border-white/[0.06] pt-2">
            <ProtoBtn size="sm" className="flex-1" disabled={busy} onClick={() => void handleApplyLast()}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {applyButtonLabel(lastWritableAssistant.outputKind, lastWritableAssistant.skill)}
            </ProtoBtn>
          </div>
        ) : null}
        {lastWritableAssistant && !canApplyAssistant(lastWritableAssistant) ? (
          <p className="mt-2 shrink-0 text-[10px] text-zinc-500">{t('writer.editor.agentFinalizedHint')}</p>
        ) : null}
      </ProtoCard>

      <ProtoCard className="mt-auto flex max-h-[52%] min-h-[220px] shrink-0 flex-col overflow-visible !p-2.5">
        <div className="flex shrink-0 flex-wrap gap-1">
          {skills.map((skill) => {
            const blocked = blockReasonFor(skill)
            return (
              <button
                key={skill}
                type="button"
                disabled={busy}
                title={blocked ? t(blocked) : undefined}
                onClick={() => {
                  setActiveSkill(skill)
                  if (blocked) setError(t(blocked))
                  else setError(null)
                }}
                className={`rounded-md px-2 py-1 text-[10px] font-medium transition ${
                  blocked
                    ? 'cursor-not-allowed bg-white/[0.03] text-zinc-600'
                    : activeSkill === skill
                      ? 'bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-500/30'
                      : 'bg-white/[0.04] text-zinc-400 hover:bg-white/[0.08] hover:text-zinc-200'
                }`}
              >
                {skillLabel(skill)}
              </button>
            )
          })}
        </div>

        <div className="mt-2 flex shrink-0 items-center gap-1.5 text-xs font-medium text-zinc-200">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
          {t('writer.editor.agentInputTitle')}
        </div>

        <ProtoTextarea
          className="studio-scrollbar-hidden mt-2 h-0 min-h-[5rem] min-w-0 flex-1 !text-xs"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t(`writer.editor.agentInputPlaceholder.${layer}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void handleSend()
            }
          }}
        />

        <div className="relative z-20 mt-2 shrink-0 overflow-visible">
          <ScriptAIModelField
            {...scriptModel}
            disabled={busy}
            compact
            inline
            trailing={
              <ProtoBtn
                size="sm"
                className="h-9 shrink-0"
                disabled={
                  busy ||
                  !aiModelReady ||
                  !!blockReasonFor(activeSkill) ||
                  (skillNeedsPrompt(activeSkill) && !prompt.trim())
                }
                onClick={() => void handleSend()}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {costLabel(t('writer.editor.agentSend'))}
              </ProtoBtn>
            }
          />
        </div>

        {error ? (
          <p className="mt-1.5 flex shrink-0 items-start gap-1.5 text-[10px] text-red-300">
            <span className="min-w-0 flex-1">{error}</span>
            <Button
              type="button"
              variant="link"
              size="icon-xs"
              className="shrink-0 text-red-400/80 hover:text-red-200"
              onClick={() => setError(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </p>
        ) : null}
      </ProtoCard>

      <ConfirmDialog
        open={clearChatOpen}
        title={t('writer.editor.agentClearChatTitle')}
        message={t('writer.editor.agentClearChatConfirm')}
        icon={Trash2}
        destructive
        confirmLabel={t('writer.editor.agentClearChat')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          persistMessages([])
          setClearChatOpen(false)
        }}
        onCancel={() => setClearChatOpen(false)}
      />
    </div>
  )
}
